// src/pages/Markets.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useSEO } from "../SEO";
import MarketTable from "./MarketTable";
import CoinInfo from "./CoinInfo";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CategoryIcon from "@mui/icons-material/Category";
import RefreshIcon from "@mui/icons-material/Refresh";

import { getTrending, getCategories } from "../coingecko";

const ACCENT = "#F0B90B";
const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const CURRENCIES = ["USD", "EUR", "TRY"];

export default function Markets() {
  useSEO?.({
    title:
      "Markets — 1CryptoX | Live Crypto Prices, Categories, Gainers/Losers",
    description:
      "Explore all crypto assets with live prices, movers, categories, and trends. Professional-grade, responsive markets overview.",
    keywords: [
      "crypto markets",
      "all coins",
      "categories",
      "gainers",
      "losers",
      "trending",
    ],
    canonical: "https://www.1cryptox.com/markets",
  });

  const [filter, setFilter] = useState("all"); // all | gainers | losers | volume
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [currency, setCurrency] = useState(
    localStorage.getItem("markets.vs") || "USD"
  );
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState(""); // CoinGecko category id
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    localStorage.setItem("markets.vs", currency);
  }, [currency]);

  // Fetch Categories + Trending
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cats = await getCategories(
          {},
          { onRefresh: (fresh) => alive && setCategories(fresh || []) }
        );
        alive && setCategories(cats || []);
      } catch {}
      try {
        const t = await getTrending(
          {},
          { onRefresh: (fresh) => alive && setTrending(fresh?.coins || []) }
        );
        alive && setTrending(t?.coins || []);
      } catch {}
    })();
    return () => (alive = false);
  }, []);

  const chips = useMemo(
    () => [
      { key: "all", label: "All" },
      { key: "gainers", label: "Top Gainers" },
      { key: "losers", label: "Top Losers" },
      { key: "volume", label: "High Volume" },
    ],
    []
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* HERO / Header */}
        <Card
          sx={{
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
            mb: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(70% 60% at 80% 20%, rgba(240,185,11,0.12) 0%, rgba(240,185,11,0.02) 100%)",
              pointerEvents: "none",
            }}
          />
          <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
            <Typography
              variant="overline"
              sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 900 }}
            >
              Markets
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, mb: 0.5, color: TEXT }}
            >
              Live Crypto Overview
            </Typography>
            <Typography sx={{ color: MUTED, maxWidth: 900 }}>
              Explore all assets with real-time prices, 24h change, volumes, and
              sector snapshots. Filter movers, scan liquidity, and deep-dive
              into asset profiles.
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.25, flexWrap: "wrap", alignItems: "center" }}
            >
              {chips.map((c) => (
                <Chip
                  key={c.key}
                  label={c.label}
                  onClick={() => setFilter(c.key)}
                  variant={filter === c.key ? "filled" : "outlined"}
                  color={filter === c.key ? "primary" : "default"}
                  sx={{
                    fontWeight: filter === c.key ? 800 : 600,
                    bgcolor:
                      filter === c.key
                        ? "rgba(240,185,11,0.15)"
                        : "transparent",
                    color: filter === c.key ? ACCENT : TEXT,
                    borderColor:
                      filter === c.key ? "rgba(240,185,11,0.35)" : BORDER,
                  }}
                />
              ))}

              {/* Currency select */}
              <FormControl
                size="small"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: 120 }}
              >
                <InputLabel sx={{ color: MUTED }}>Currency</InputLabel>
                <Select
                  value={currency}
                  label="Currency"
                  onChange={(e) => setCurrency(e.target.value)}
                  sx={{
                    color: TEXT,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  }}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Category select */}
              <FormControl
                size="small"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: 180 }}
              >
                <InputLabel sx={{ color: MUTED }}>
                  <CategoryIcon sx={{ mr: 0.5 }} /> Category
                </InputLabel>
                <Select
                  value={catId}
                  label="Category"
                  onChange={(e) => setCatId(e.target.value)}
                  displayEmpty
                  sx={{
                    color: TEXT,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.slice(0, 60).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Reset filters">
                <IconButton
                  onClick={() => {
                    setFilter("all");
                    setCatId("");
                  }}
                  sx={{ color: ACCENT }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>

        {/* Highlight sections */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Trending */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <WhatshotIcon sx={{ color: ACCENT }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: TEXT }}
                  >
                    Trending Now
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {trending.slice(0, 6).map((t) => (
                    <Stack
                      key={t.item.coin_id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ color: TEXT, opacity: 0.9 }}
                    >
                      <img
                        src={t.item.small}
                        alt={t.item.symbol}
                        width={22}
                        height={22}
                      />
                      <Typography sx={{ fontWeight: 700 }}>
                        {t.item.name}
                        <Typography
                          component="span"
                          sx={{
                            ml: 0.5,
                            opacity: 0.6,
                            textTransform: "uppercase",
                          }}
                        >
                          {t.item.symbol}
                        </Typography>
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Gainers / Losers (24h) — ملاحظة: سنحسبها من جدول السوق نفسه عبر props */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <TrendingUpIcon sx={{ color: ACCENT }} />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: TEXT }}
                      >
                        Top Gainers (24h)
                      </Typography>
                    </Stack>
                    <MarketTable
                      dense
                      limit={5}
                      filterMode="gainers"
                      vs={currency}
                      category={catId}
                      onSelect={(coin) => setSelectedCoin(coin)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <TrendingDownIcon sx={{ color: ACCENT }} />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: TEXT }}
                      >
                        Top Losers (24h)
                      </Typography>
                    </Stack>
                    <MarketTable
                      dense
                      limit={5}
                      filterMode="losers"
                      vs={currency}
                      category={catId}
                      onSelect={(coin) => setSelectedCoin(coin)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main table + Side panel */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, mb: 1, color: TEXT }}
                >
                  All Assets
                </Typography>
                <MarketTable
                  vs={currency}
                  category={catId}
                  filterMode={filter}
                  onSelect={(coin) => setSelectedCoin(coin)}
                  pageable
                />
                <Divider sx={{ my: 2, borderColor: BORDER }} />
                <Typography variant="caption" sx={{ color: MUTED }}>
                  * Data provided by CoinGecko — paginated (250/pg), refreshed
                  via SWR with backoff.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: "100%",
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, mb: 1, color: TEXT }}
                >
                  Asset Details
                </Typography>
                <Typography sx={{ color: MUTED, mb: 2 }}>
                  Click any asset to view profile, quick stats, and official
                  links.
                </Typography>
                <CoinInfo coin={selectedCoin} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
